import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { DateTime } from "luxon";

import { useScreenWatcher } from "@common/screenWatcher";
import { HOURS_PER_DAY, Views } from "../api/views";
import { useScrollPos } from "../scrollPosContext";
import { Props } from "./types";
import { getDaysTexts, isRestDay } from "./utils";
import { Months } from "./months";
import { Years } from "./years";
import { DailyMonths } from "./dailyMonths";
import styles from "./styles.scss";

function isNow(dt: DateTime) {
  return dt.hasSame(DateTime.now(), "day");
}

function isMonthLastDay(dt: DateTime) {
  return dt.plus({ days: 1 }).month !== dt.month;
}

function isYearLastDay(dt: DateTime) {
  return dt.plus({ days: 1 }).year !== dt.year;
}

const headerMap = {
  [Views.DAILY]: DailyMonths,
  [Views.WEEKLY]: Months,
  [Views.MONTHLY]: Months,
  [Views.YEARLY]: Years,
};

// Hour labels for the daily view, precomputed once.
const HOURS = Array.from({ length: HOURS_PER_DAY }, (_, hour) =>
  hour < 10 ? `0${hour}` : String(hour),
);

// Zooming out shrinks the day cell; below these per-hour widths the
// subdivision stops reading as columns — drop the labels first, then the
// gridlines, leaving plain day columns.
const MIN_HOUR_LABEL_WIDTH = 15;
const MIN_HOUR_LINE_WIDTH = 5;

function Calendar({ dates, cell, currentView, height }: Props) {
  const { t } = useTranslation();
  const screen = useScreenWatcher();
  const days = getDaysTexts(t);
  const Header = headerMap[currentView];
  const width = cell.width * dates.length;
  const startIndex = useScrollPos();
  const paddedStartIndex = startIndex - 10 <= 0 ? 0 : startIndex - 10;
  const endIndex = startIndex + Math.round(screen.width / cell.width);
  const datesChunk = dates.slice(paddedStartIndex, endIndex);

  return (
    <div
      className={styles.calendar}
      style={{
        height:
          height +
          (cell.height +
            1 +
            (currentView === Views.DAILY
              ? cell.height * 2
              : cell.height + cell.height * (1 / 3))),
        marginLeft: paddedStartIndex * cell.width,
        width,
      }}
    >
      <div className={styles.header}>
        <Header dates={datesChunk} useWeeks={currentView === Views.MONTHLY} />
      </div>
      <div className={styles.dates}>
        {datesChunk.map((dt) => {
          // Luxon weekday: 1=Mon..7=Sun. Convert to 0=Mon..6=Sun for the
          // existing `days` array indexing.
          const dayIndex = dt.weekday - 1;
          const name = days[dayIndex].slice(0, 3);
          const day = dt.day;
          const isRest = isRestDay(dayIndex);
          const isYearLD = isYearLastDay(dt);

          return (
            <div
              key={dt.toFormat("yyyy-MM-dd")}
              className={classNames(styles.date, {
                [styles.restDay]: isRest,
                [styles.lastRestDay]: isRest && dayIndex === 6,
                [styles.now]: isNow(dt),
                [styles.daily]: currentView === Views.DAILY,
                [styles.monthly]: currentView === Views.MONTHLY,
                [styles.yearly]: currentView === Views.YEARLY,
                [styles.lastMonthDay]: isMonthLastDay(dt),
                [styles.lastYearDay]: isYearLD,
                [styles.weekEndDay]: dayIndex === 6,
              })}
              style={{
                width: cell.width,
                height:
                  height +
                  (cell.height +
                    1 +
                    (currentView === Views.DAILY
                      ? cell.height * 2
                      : cell.height + cell.height * (1 / 3))),
              }}
            >
              {currentView === Views.DAILY ? (
                <>
                  <p
                    className={styles.heading}
                    style={{
                      height: cell.height,
                      top: cell.height + 1,
                    }}
                  >
                    <span className={styles.dayName}>
                      {name} {day < 10 ? `0${day}` : day}
                    </span>
                  </p>
                  {cell.width / HOURS_PER_DAY >= MIN_HOUR_LINE_WIDTH && (
                    <div className={styles.hours}>
                      {HOURS.map((hour) => (
                        <div key={hour} className={styles.hour}>
                          {cell.width / HOURS_PER_DAY >=
                            MIN_HOUR_LABEL_WIDTH && (
                            <p
                              className={styles.hourLabel}
                              style={{ height: cell.height }}
                            >
                              {hour}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                currentView !== Views.MONTHLY &&
                currentView !== Views.YEARLY && (
                  <p
                    className={styles.heading}
                    style={{
                      height: cell.height + cell.height * (1 / 3),
                      top: cell.height + 1,
                    }}
                  >
                    <span className={styles.weeklyName}>{name}</span>
                    <span>{day < 10 ? `0${day}` : day}</span>
                  </p>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { Calendar };
